import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do pest control companies get more calls in Colorado?",
    a: "The fastest path is a tuned Google Business Profile + dedicated landing pages for each pest (mice, wasps, spiders, bed bugs, mosquitoes) and each season. 'Exterminator near me' searches spike seasonally, and the pest control company that ranks first in the Map Pack captures the urgent calls.",
  },
  {
    q: "What's the best marketing channel for a Colorado pest control company?",
    a: "Local SEO + Google reviews is the highest-ROI channel — pest emergencies are local and urgent. Google Ads is essential during spring/summer surges (May–August). Recurring quarterly plans are built on SEO, referrals, and tight follow-up automation.",
  },
  {
    q: "Should I build separate pages for each pest?",
    a: "Yes. 'Wasp removal Colorado Springs', 'mice exterminator Denver', and 'bed bug treatment near me' are all distinct searches with distinct intent. One generic 'pest control services' page leaves money on the table to competitors who built dedicated pages.",
  },
  {
    q: "How long does pest control SEO take to work?",
    a: "Most Colorado pest control companies see Map Pack movement in 60–120 days. Seasonal terms (wasps in summer, mice in fall) can rank faster if you publish dedicated pages 60 days before the season hits.",
  },
  {
    q: "Do you handle commercial pest control marketing?",
    a: "Yes — and commercial (restaurants, multifamily, warehouses) is the highest-margin segment most pest control companies under-market. Dedicated commercial landing pages with health-code compliance info and after-hours service convert restaurant and property-management buyers.",
  },
];

export const Route = createFileRoute("/pest-control-marketing-colorado")({
  head: () =>
    industryMarketingHead("Pest Control", "pest control company", "pest-control-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Pest Control"
      industryLower="pest control company"
      slug="pest-control-marketing-colorado"
      subheadline="We help Colorado pest control companies fill seasonal surges and build recurring quarterly residential and commercial routes."
      painPoints={[
        "Spring wasp/ant surge passes and the phone never rings as much as it should",
        "Commercial restaurant and multifamily bids go to whoever ranks first locally",
        "One generic 'pest control' page is invisible for 'mice exterminator' and 'bed bug treatment' searches",
      ]}
      serviceDescriptions={{
        seo: "Rank for every pest, every season, every Colorado market — mice, wasps, spiders, bed bugs, mosquitoes, and commercial.",
        googleMaps: "Dominate the local Map Pack so urgent 'exterminator near me' calls come to you first.",
        webDesign: "Fast, mobile-first pest control sites with one-tap calling and instant quote forms.",
        ppc: "Profitable Google Ads tuned to seasonal surges, emergency pests, and high-margin commercial.",
        geoAi: "Get cited when Coloradans ask ChatGPT 'best exterminator near me' or 'how to get rid of mice'.",
        socialMedia: "Local trust-building content for recurring residential quarterly plan upsells.",
      }}
      testimonial={{
        quote:
          "Our wasp season went from chaotic to fully booked. The seasonal landing pages they built rank before the bugs even show up.",
        role: "Pest Control Owner",
        city: "Colorado Springs",
      }}
      faqs={FAQS}
      cityHubLinks={[
        { to: "/colorado-springs", label: "Pest control marketing in Colorado Springs", description: "Help Springs pest control companies own seasonal demand across Briargate, Black Forest, and Fountain." },
        { to: "/denver-digital-marketing", label: "Pest control marketing in Denver", description: "Compete in Denver's residential and commercial pest market — neighborhood by neighborhood." },
        { to: "/castle-rock-digital-marketing", label: "Pest control marketing in Castle Rock", description: "Premium residential and HOA quarterly pest plans across Castle Rock and Castle Pines." },
      ]}
    />
  ),
});
